import { ShieldCheck } from 'lucide-react';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';

type OAuthRequest = Readonly<{
  clientId: string;
  redirectUri: string;
  state: string;
  scope: string;
  codeChallenge: string;
  codeChallengeMethod: 'S256';
  clientHost: string;
}>;

function readOAuthRequest(search: string): OAuthRequest | undefined {
  const params = new URLSearchParams(search);
  const clientId = params.get('client_id') ?? '';
  const redirectUri = params.get('redirect_uri') ?? '';
  const state = params.get('state') ?? '';
  const scope = params.get('scope') ?? '';
  const codeChallenge = params.get('code_challenge') ?? '';
  const codeChallengeMethod = params.get('code_challenge_method');
  if (params.get('response_type') !== 'code' || codeChallengeMethod !== 'S256'
    || !clientId || !redirectUri || !state || !scope || !codeChallenge) return undefined;
  try {
    const redirect = new URL(redirectUri);
    if (redirect.hash || redirect.username || redirect.password) return undefined;
    return {
      clientId,
      redirectUri,
      state,
      scope,
      codeChallenge,
      codeChallengeMethod,
      clientHost: redirect.host,
    };
  } catch {
    return undefined;
  }
}

export function OAuthAuthorizePage(): ReactNode {
  const auth = useAuth();
  const location = useLocation();
  const request = useMemo(() => readOAuthRequest(location.search), [location.search]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function signIn(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await auth.signIn(email, password);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось войти');
    } finally {
      setBusy(false);
    }
  }

  async function approve(): Promise<void> {
    if (!request || !auth.session) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch('/api/v1/oauth/authorize', {
        method: 'POST',
        headers: {
          authorization: `Bearer ${auth.session.accessToken}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify(request),
      });
      const payload = await response.json().catch(() => undefined) as {
        redirectUrl?: string;
        error_description?: string;
      } | undefined;
      if (!response.ok || !payload?.redirectUrl) {
        throw new Error(payload?.error_description ?? 'Не удалось разрешить подключение');
      }
      window.location.assign(payload.redirectUrl);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось разрешить подключение');
      setBusy(false);
    }
  }

  if (!request) {
    return <main className="auth-page"><section className="auth-card"><h1>Некорректный запрос подключения</h1><p>Вернитесь в ChatGPT или Claude и запустите подключение заново.</p></section></main>;
  }
  if (auth.loading) return <main className="loading-page">Проверяем аккаунт…</main>;

  return (
    <main className="auth-page oauth-authorize-page">
      <section className="auth-card oauth-authorize-card" aria-labelledby="oauth-title">
        <div className="auth-brand">Echo</div>
        <p className="page-eyebrow">Безопасное подключение</p>
        <h1 id="oauth-title">Подключить Echo CRM</h1>
        <p>ChatGPT или Claude запрашивает доступ к CRM от вашего имени.</p>
        <div className="oauth-permission">
          <ShieldCheck size={20} aria-hidden />
          <div><strong>Персональные права сохраняются</strong><span>AI увидит только компании, сделки и задачи, доступные вашему CRM-профилю.</span></div>
        </div>
        <p className="oauth-client">Возврат после входа: <strong>{request.clientHost}</strong></p>
        {!auth.session ? (
          <form onSubmit={signIn}>
            <label>Рабочий email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
            <label>Пароль CRM<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
            {error ? <div className="form-error" role="alert">{error}</div> : null}
            <button className="button button-primary" disabled={busy}>{busy ? 'Входим…' : 'Войти и продолжить'}</button>
          </form>
        ) : (
          <div className="oauth-approval">
            <div className="oauth-user"><span>{auth.session.actor.name}</span><small>{auth.session.actor.email}</small></div>
            {error ? <div className="form-error" role="alert">{error}</div> : null}
            <button className="button button-primary" type="button" disabled={busy} onClick={() => void approve()}>
              {busy ? 'Подключаем…' : 'Разрешить доступ'}
            </button>
            <button className="button button-secondary" type="button" disabled={busy} onClick={() => void auth.signOut()}>
              Войти другим аккаунтом
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
